// Growth events + attribution capture (2026-09-03, activation bundle).
//
// track(event, props)   → POST /api/growth/event via sendBeacon (fallback fetch),
//                          never awaited, never throws.
// captureAttribution()  → on the first page load of a visit, remembers UTM
//                          params, landing path and referrer in localStorage
//                          (30 days) so the `signup` event can carry them.
// trackSignupOnce(id)   → fires `signup` with the stored attribution once per
//                          user per browser; the server dedupes per user too.
//
// The user id is taken from the Clerk global when present, so call sites do
// not need to thread it through. Everything here is fail-open by design.

const KEY = 'ads_attr';
const SIGNUP_KEY = 'ads_signup_sent';
const TTL_MS = 30 * 24 * 3600 * 1000;

const safe = (fn) => { try { return fn(); } catch (_) { return undefined; } };

export const captureAttribution = () => safe(() => {
  if (typeof window === 'undefined') return;
  const existing = JSON.parse(localStorage.getItem(KEY) || 'null');
  if (existing && existing.capturedAt && Date.now() - existing.capturedAt < TTL_MS) return;
  const q = new URLSearchParams(window.location.search);
  const attr = {
    utm_source: q.get('utm_source') || q.get('src') || null,
    utm_medium: q.get('utm_medium') || null,
    utm_campaign: q.get('utm_campaign') || null,
    landing: window.location.pathname.slice(0, 120),
    referrer: (document.referrer || '').slice(0, 160) || null,
    capturedAt: Date.now()
  };
  // Keep a referrer-only record too: organic vs direct is still attribution.
  localStorage.setItem(KEY, JSON.stringify(attr));
});

export const getAttribution = () => safe(() => JSON.parse(localStorage.getItem(KEY) || 'null')) || null;

const currentUserId = () => safe(() => window.Clerk?.user?.id) || null;

export const track = (event, props = {}, userId = null) => safe(() => {
  if (typeof window === 'undefined') return;
  const payload = JSON.stringify({ userId: userId || currentUserId(), event, props });
  const url = '/api/growth/event';
  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'text/plain' });
    if (navigator.sendBeacon(url, blob)) return;
  }
  fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
});

export const trackSignupOnce = (userId) => safe(() => {
  if (!userId) return;
  const sent = JSON.parse(localStorage.getItem(SIGNUP_KEY) || '{}');
  if (sent[userId]) return;
  const attr = getAttribution() || {};
  track('signup', {
    utm_source: attr.utm_source || null,
    utm_medium: attr.utm_medium || null,
    utm_campaign: attr.utm_campaign || null,
    landing: attr.landing || null,
    referrer: attr.referrer || null
  }, userId);
  sent[userId] = Date.now();
  localStorage.setItem(SIGNUP_KEY, JSON.stringify(sent));
});

// Capture on import so every entry point (marketing pages, tool pages,
// sign-up redirects) records the first touch without wiring.
captureAttribution();
